import React from 'react'

import {
    IconButton,
    Popover,
    Theme,
    Tooltip,
    useTheme,
} from '@material-ui/core'
import CloseIcon from '@material-ui/icons/Close'
import ArrowRightAltIcon from '@material-ui/icons/ArrowRightAlt'

import { SupportTicketTransition, SupportTicketStatus } from '../models/types'
import { Container } from './Container'
import { AvatarCircle } from './AvatarCircle'
import { SupportTicketStatusBadge } from './SupportTicketStatusBadge'
import { toMMDDYYYY, toUiTime } from '../helpers'
import { typographyStyle } from '../theme/typographyStyle'

interface Props {
    open: boolean
    anchorEl: HTMLElement | null
    closePopover?: (
        event: {},
        reason: 'backdropClick' | 'escapeKeyDown',
    ) => void
    transitions: SupportTicketTransition[] | null
    createdDate: string
}

export const SupportTicketTransitionLogPopover = (props: Props) => {
    const { open, anchorEl, closePopover, transitions, createdDate } = props
    const theme = useTheme()

    return (
        <Popover
            open={open}
            anchorEl={anchorEl}
            anchorOrigin={{
                horizontal: 'left',
                vertical: 'bottom',
            }}
            onClose={closePopover}
        >
            <Container
                style={{
                    padding: theme.spacing(1),
                    flexDirection: 'column',
                    maxHeight: 500,
                }}
            >
                <Container style={{ alignItems: 'center' }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.h6),
                            fontWeight: 700,
                        }}
                    >
                        All Transitions
                    </span>
                    <div style={{ flex: 1 }} />
                    <Tooltip title="Close">
                        <IconButton
                            onClick={(e) =>
                                closePopover && closePopover(e, 'backdropClick')
                            }
                        >
                            <CloseIcon fontSize="large" />
                        </IconButton>
                    </Tooltip>
                </Container>

                <Container style={{ flexDirection: 'column' }} scrollY>
                    <Container>
                        <span
                            style={{
                                ...typographyStyle(theme.typography.subtitle2),
                                fontWeight: 700,
                            }}
                        >
                            Created: {toMMDDYYYY(new Date(createdDate))}
                        </span>
                    </Container>
                    {transitions?.map((transition, idx) => (
                        <TransitionLog
                            key={`TICKET_TRANSITION_${transition.id}`}
                            transition={transition}
                            theme={theme}
                            marginBottom={
                                idx < transitions.length - 1
                                    ? theme.spacing(1)
                                    : 0
                            }
                        />
                    ))}
                </Container>
            </Container>
        </Popover>
    )
}

interface TLProps {
    transition: SupportTicketTransition
    theme: Theme
    marginBottom?: number
}

const TransitionLog = (props: TLProps) => {
    const { transition, theme } = props

    return (
        <Container
            style={{
                backgroundColor: theme.palette.grey[200],
                border: `1px solid ${theme.palette.grey[400]}`,
                borderRadius: theme.shape.borderRadius,
                padding: theme.spacing(0.5),
                marginBottom: props.marginBottom,
            }}
        >
            <AvatarCircle
                initials
                text={transition.user.name}
                tooltip={transition.user.name}
            />

            <Container style={{ flexDirection: 'column' }}>
                <Container style={{ marginLeft: theme.spacing(1) }}>
                    <SupportTicketStatusBadge
                        status={transition.from_status}
                        style={{
                            text: {
                                fontWeight: 700,
                            },
                        }}
                    />

                    <ArrowRightAltIcon fontSize="large" />

                    <SupportTicketStatusBadge
                        status={transition.to_status}
                        style={{
                            text: {
                                fontWeight: 700,
                            },
                            container: {
                                marginLeft: theme.spacing(1),
                            },
                        }}
                    />
                </Container>

                <Container style={{ marginLeft: theme.spacing(1) }}>
                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            fontWeight: 300,
                        }}
                    >
                        {toMMDDYYYY(new Date(transition.created_date))}
                    </span>

                    <span
                        style={{
                            ...typographyStyle(theme.typography.subtitle2),
                            fontWeight: 300,
                            marginLeft: theme.spacing(1),
                        }}
                    >
                        {toUiTime(new Date(transition.created_date))}
                    </span>
                </Container>
            </Container>
        </Container>
    )
}
